import type { StoryNode, StoryProject } from './types'
import { describeCondition, describeEffect } from './storyLogic'

export type DraftDifference = { label: string; before: string; after: string }
export type SceneDifference = {
  nodeId: string
  title: string
  kind: 'added' | 'removed' | 'changed'
  fields: DraftDifference[]
}

function sceneFields(
  node: StoryNode,
  project: StoryProject,
): Record<string, string> {
  const variables = project.variables ?? []
  const destination = (id: string | null) =>
    project.nodes.find((scene) => scene.id === id)?.title ?? '[unconnected]'
  return {
    Title: node.title,
    'Draft status': node.status ?? 'draft',
    Chapter: node.chapter ?? '',
    'Scene intention': node.synopsis ?? '',
    'Writer’s notes': node.notes ?? '',
    Script: node.blocks
      .map(
        (block) =>
          (block.kind === 'dialogue'
            ? `${block.speaker || 'CHARACTER'}${block.direction ? ' (' + block.direction + ')' : ''}\n`
            : '') + block.text,
      )
      .join('\n\n'),
    Routes:
      node.kind === 'ending'
        ? 'THE END'
        : node.choices.length
          ? node.choices
              .map((choice) => {
                const rules = (choice.conditions ?? [])
                  .map((condition) => describeCondition(condition, variables))
                  .join(choice.conditionMode === 'any' ? ' or ' : ' and ')
                const effects = (choice.effects ?? [])
                  .map((effect) => describeEffect(effect, variables))
                  .join('; ')
                return `${choice.label} → ${destination(choice.targetId)}${rules ? '\nWhen ' + rules + ' · otherwise ' + (choice.unavailableMode === 'hide' ? 'hidden' : 'locked') : ''}${effects ? '\n' + effects : ''}`
              })
              .join('\n\n')
          : 'Continue → ' + destination(node.nextId),
    'Scene entry changes': (node.effects ?? [])
      .map((effect) => describeEffect(effect, variables))
      .join('\n'),
  }
}

function differences(
  before: Record<string, string>,
  after: Record<string, string>,
) {
  return Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
    .filter((label) => (before[label] ?? '') !== (after[label] ?? ''))
    .map((label) => ({
      label,
      before: before[label] ?? '',
      after: after[label] ?? '',
    }))
}

function bibleDetail(value: unknown) {
  return JSON.stringify(
    value,
    (_key, item) => {
      if (typeof item !== 'string' || !item.startsWith('data:image/'))
        return item
      let fingerprint = 2166136261
      for (let i = 0; i < item.length; i++)
        fingerprint = Math.imul(fingerprint ^ item.charCodeAt(i), 16777619)
      return '[Image ' + (fingerprint >>> 0).toString(16) + ']'
    },
    2,
  )
}
function projectFields(project: StoryProject): Record<string, string> {
  return {
    'Story title': project.title,
    'Opening scene':
      project.nodes.find((node) => node.id === project.startNodeId)?.title ??
      project.startNodeId,
    'Scene order': project.nodes.map((node) => node.id).join(', '),
    Premise: project.bible?.premise ?? '',
    'World & rules': project.bible?.world ?? '',
    Notebook: project.bible?.notes ?? '',
    Characters: (project.bible?.characters ?? [])
      .map((character) =>
        [character.name, character.role, character.description, character.voice]
          .filter(Boolean)
          .join('\n'),
      )
      .join('\n\n'),
    'Bible sections & entries': bibleDetail({
      sections: project.bible?.sections || [],
      entries: project.bible?.entries || [],
    }),
    Connections: bibleDetail(project.bible?.links || []),
    'Character images & references': bibleDetail(
      (project.bible?.characters || []).map((c) => ({
        name: c.name,
        images: c.images || [],
        references: c.references || [],
      })),
    ),
    'Story variables': (project.variables ?? [])
      .map(
        (variable) =>
          `${variable.name} (${variable.type}): ${String(variable.initialValue)}${variable.description ? '\n' + variable.description : ''}`,
      )
      .join('\n\n'),
    'Word goal': project.wordGoal ? String(project.wordGoal) : '',
  }
}

export function compareDrafts(before: StoryProject, after: StoryProject) {
  const scenes: SceneDifference[] = []
  for (const previous of before.nodes) {
    const current = after.nodes.find((node) => node.id === previous.id)
    const fields = differences(
      sceneFields(previous, before),
      current ? sceneFields(current, after) : {},
    )
    if (!current || fields.length)
      scenes.push({
        nodeId: previous.id,
        title: current?.title ?? previous.title,
        kind: current ? 'changed' : 'removed',
        fields,
      })
  }
  for (const current of after.nodes) {
    if (!before.nodes.some((node) => node.id === current.id))
      scenes.push({
        nodeId: current.id,
        title: current.title,
        kind: 'added',
        fields: differences({}, sceneFields(current, after)),
      })
  }
  return {
    scenes,
    project: differences(projectFields(before), projectFields(after)),
  }
}
